import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { companyOpportunityAPI } from '../../api/axios';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';
import { ArrowLeft, LogOut, Loader2 } from 'lucide-react';

const inp = "w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 bg-gray-50";

const SHIFT_OPTIONS = [2, 4, 6, 8, 12, 24];

export default function PostOpportunity() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    title: '', roleName: '', description: '',
    shiftHours: '', dailyRateMin: '', dailyRateMax: '',
    city: '', state: '', willingToRelocate: false,
    requiredFrom: '', requiredUntil: '', applicationDeadline: '',
    experienceRequired: '', qualificationRequired: '',
    status: 'OPEN',
  });

  const set = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(f => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    if (!form.roleName.trim()) { toast.error('Role name is required'); return; }
    setSaving(true);
    try {
      const payload = { ...form };
      ['shiftHours', 'dailyRateMin', 'dailyRateMax', 'experienceRequired'].forEach(k => {
        if (payload[k] === '') delete payload[k];
        else if (k === 'shiftHours' || k === 'experienceRequired') payload[k] = parseInt(payload[k]);
        else payload[k] = parseFloat(payload[k]);
      });
      ['requiredFrom', 'requiredUntil', 'applicationDeadline'].forEach(k => {
        if (payload[k] === '') delete payload[k];
      });
      await companyOpportunityAPI.create(payload);
      toast.success('Opportunity posted!');
      navigate('/company/opportunities');
    } catch (e) { toast.error(e.response?.data?.message || 'Failed to post'); }
    finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <nav className="bg-white border-b border-gray-100 shadow-sm sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/company/opportunities')}
              className="text-gray-400 hover:text-gray-600 p-1 rounded-lg hover:bg-gray-100 transition">
              <ArrowLeft className="h-5 w-5" />
            </button>
            <img src="/logo-horizontal.png" alt="ShramSena" className="h-7 w-auto" />
          </div>
          <button onClick={() => { logout(); navigate('/company/login'); }}
            className="flex items-center gap-1.5 text-sm font-medium text-red-500 hover:text-red-700">
            <LogOut className="h-4 w-4" /> Logout
          </button>
        </div>
      </nav>

      <div className="max-w-3xl mx-auto px-6 py-8">
        <div className="mb-6">
          <h1 className="text-xl font-bold text-slate-800">Post an Opportunity</h1>
          <p className="text-sm text-gray-400 mt-0.5">Verified staff will be able to apply</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">

          {/* Basic info */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 space-y-4">
            <p className="text-xs font-bold text-slate-500 uppercase tracking-widest">Basic Information</p>
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1.5">Job Title *</label>
              <input name="title" value={form.title} onChange={set} required
                placeholder="e.g. Registered Nurse for ICU" className={inp} />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1.5">Role / Specialization *</label>
              <input name="roleName" value={form.roleName} onChange={set} required
                placeholder="e.g. Registered Nurse, Caregiver, Physiotherapist" className={inp} />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1.5">Description</label>
              <textarea name="description" value={form.description} onChange={set}
                placeholder="Describe the role, responsibilities, and any specific requirements..."
                rows={3}
                className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm bg-gray-50 focus:outline-none focus:ring-2 focus:ring-teal-500 resize-none" />
            </div>
          </div>

          {/* Requirements */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 space-y-4">
            <p className="text-xs font-bold text-slate-500 uppercase tracking-widest">Requirements</p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">Qualification</label>
                <input name="qualificationRequired" value={form.qualificationRequired} onChange={set}
                  placeholder="e.g. GNM, ANM, B.Pharm" className={inp} />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">Min Experience (years)</label>
                <input name="experienceRequired" type="number" value={form.experienceRequired} onChange={set}
                  placeholder="0" min={0} className={inp} />
              </div>
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-2">Shift Hours Required</label>
              <div className="flex gap-2 flex-wrap">
                {SHIFT_OPTIONS.map(h => (
                  <button key={h} type="button"
                    onClick={() => setForm(f => ({ ...f, shiftHours: f.shiftHours === h ? '' : h }))}
                    className={`px-4 py-2 rounded-xl text-sm font-semibold border transition
                      ${form.shiftHours === h
                        ? 'bg-teal-600 text-white border-teal-600'
                        : 'bg-white text-gray-600 border-gray-200 hover:border-teal-400'}`}>
                    {h}hr
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Compensation */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 space-y-4">
            <p className="text-xs font-bold text-slate-500 uppercase tracking-widest">Daily Rate (₹)</p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">Minimum</label>
                <input name="dailyRateMin" type="number" value={form.dailyRateMin} onChange={set}
                  placeholder="800" className={inp} />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">Maximum</label>
                <input name="dailyRateMax" type="number" value={form.dailyRateMax} onChange={set}
                  placeholder="1500" className={inp} />
              </div>
            </div>
          </div>

          {/* Location */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 space-y-4">
            <p className="text-xs font-bold text-slate-500 uppercase tracking-widest">Location</p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">City</label>
                <input name="city" value={form.city} onChange={set} placeholder="Gurgaon" className={inp} />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">State</label>
                <input name="state" value={form.state} onChange={set} placeholder="Haryana" className={inp} />
              </div>
            </div>
            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" name="willingToRelocate" checked={form.willingToRelocate} onChange={set}
                className="w-4 h-4 accent-teal-600" />
              <span className="text-sm text-slate-700">Open to candidates willing to relocate</span>
            </label>
          </div>

          {/* Duration */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 space-y-4">
            <p className="text-xs font-bold text-slate-500 uppercase tracking-widest">Timeline</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">Required From</label>
                <input name="requiredFrom" type="date" value={form.requiredFrom} onChange={set} className={inp} />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">Required Until</label>
                <input name="requiredUntil" type="date" value={form.requiredUntil} onChange={set} className={inp} />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">Application Deadline</label>
                <input name="applicationDeadline" type="date" value={form.applicationDeadline} onChange={set} className={inp} />
              </div>
            </div>
          </div>

          {/* Post as draft or live */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
            <div className="flex gap-3">
              {['OPEN', 'DRAFT'].map(s => (
                <button key={s} type="button"
                  onClick={() => setForm(f => ({ ...f, status: s }))}
                  className={`flex-1 py-2.5 rounded-xl text-sm font-bold border-2 transition
                    ${form.status === s
                      ? s === 'OPEN' ? 'border-teal-500 bg-teal-50 text-teal-700'
                        : 'border-gray-300 bg-gray-50 text-gray-700'
                      : 'border-gray-200 text-gray-500 hover:border-gray-300'}`}>
                  {s === 'OPEN' ? '🟢 Post Live' : '📝 Save as Draft'}
                </button>
              ))}
            </div>
          </div>

          <button type="submit" disabled={saving}
            className="w-full bg-gradient-to-r from-teal-600 to-cyan-600 text-white py-3.5 rounded-xl
              font-bold text-sm hover:opacity-90 transition shadow-lg shadow-teal-500/20
              flex items-center justify-center gap-2 disabled:opacity-60">
            {saving ? <><Loader2 className="h-4 w-4 animate-spin" /> Posting...</> : 'Post Opportunity'}
          </button>
        </form>
      </div>
    </div>
  );
}
